/**
 * PRD §9.4 — Job **ops** lifecycle (`ggj_b2b_jobs.status`: open / on_hold / filled / closed).
 * **Publish** / careers (`job_publish_status`) is separate — see Job detail Overview + `JobLifecycleModal`.
 */

import type { JobPublishStatus } from '@core/types';
import type { JobStatus } from '@core/enums';

/** Visual flow for JobOpsFlowStepper (2-step happy path + closed off-path). */
export const JOB_OPS_FLOW_STEPS: readonly {
  key: 'recruiting' | 'outcome';
  title: string;
  description: string;
}[] = [
  {
    key: 'recruiting',
    title: 'Recruiting',
    description: 'Job is open or on hold — run sourcing, screening, and pipeline updates.',
  },
  {
    key: 'outcome',
    title: 'Outcome',
    description: 'Mark filled when the role is staffed. Close from edit if the mandate ends without a hire.',
  },
];

/** Phase index for JOB_OPS_FLOW_STEPS, or -1 when closed (off happy path). */
export function getJobOpsFlowPhaseIndex(status: JobStatus): number {
  if (status === 'closed') return -1;
  if (status === 'filled') return 1;
  return 0;
}

export function isJobOpsFlowOffHappyPath(status: JobStatus): boolean {
  return status === 'closed';
}

export const JOB_OPS_STATUS_LABELS: Record<JobStatus, string> = {
  open: 'Open',
  on_hold: 'On hold',
  filled: 'Filled',
  closed: 'Closed',
};

/**
 * Valid one-step ops transitions (mirrors `requisitionLifecycle` pattern for §9.3).
 * Terminal: **filled** (hire complete). **closed** can reopen to **open**.
 */
export const JOB_OPS_ALLOWED_TRANSITIONS: Record<JobStatus, JobStatus[]> = {
  open: ['on_hold', 'filled', 'closed'],
  on_hold: ['open', 'closed'],
  filled: [],
  closed: ['open'],
};

export function isJobOpsTransitionAllowed(from: JobStatus, to: JobStatus): boolean {
  if (from === to) return true;
  return JOB_OPS_ALLOWED_TRANSITIONS[from]?.includes(to) ?? false;
}

/** Status values allowed in the edit dropdown for the current row. */
export function jobOpsStatusEditOptions(current: JobStatus): JobStatus[] {
  const next = JOB_OPS_ALLOWED_TRANSITIONS[current] ?? [];
  return Array.from(new Set<JobStatus>([current, ...next]));
}

export const JOB_OPS_LIFECYCLE_HELPER =
  'Ops status controls recruiting work: Open = active sourcing, On hold = pause, Filled = hire complete (no further moves). Closed = cancelled/archived; reopen to Open if the role is live again. Careers listing is controlled separately via Publish status.';

// ── Lifecycle B — Publish / careers (PRD §9.4 mermaid) ─────────────────────

/** Stepper copy for publish lifecycle (modal + `JobPublishFlowStepper`). */
export const JOB_PUBLISH_FLOW_STEPS: readonly {
  key: 'draft' | 'pending_review' | 'live' | 'delisted';
  title: string;
  description: string;
}[] = [
  {
    key: 'draft',
    title: 'Draft',
    description: 'JD exists in the ATS but is not on the public careers site yet.',
  },
  {
    key: 'pending_review',
    title: 'Client listing approval',
    description:
      'Optional gate: email the hiring client and move here while you wait for written OK to advertise the role.',
  },
  {
    key: 'live',
    title: 'Live or scheduled',
    description: 'Published on careers (with ops status open), or scheduled for auto-go-live when the worker runs.',
  },
  {
    key: 'delisted',
    title: 'Delisted',
    description: 'Paused or closed on the careers channel — candidates no longer see this posting.',
  },
];

/**
 * FE-only transition map (parity with `REQUISITION_ALLOWED_TRANSITIONS`).
 * DB does not enforce these yet (PRD §9.4 pending).
 */
export const JOB_PUBLISH_ALLOWED_TRANSITIONS: Record<JobPublishStatus, JobPublishStatus[]> = {
  draft: ['pending_review', 'scheduled', 'published', 'closed'],
  pending_review: ['draft', 'published'],
  scheduled: ['published', 'draft'],
  published: ['paused', 'closed'],
  paused: ['published', 'closed'],
  closed: ['draft'],
};

export function isJobPublishTransitionAllowed(from: JobPublishStatus, to: JobPublishStatus): boolean {
  if (from === to) return true;
  return JOB_PUBLISH_ALLOWED_TRANSITIONS[from]?.includes(to) ?? false;
}

export function jobPublishNextOptions(current: JobPublishStatus | null): JobPublishStatus[] {
  const c = current ?? 'draft';
  const next = JOB_PUBLISH_ALLOWED_TRANSITIONS[c] ?? [];
  return Array.from(new Set<JobPublishStatus>([c, ...next]));
}

export const JOB_PUBLISH_LIFECYCLE_HELPER =
  'Publish is separate from ops: you can run recruiting while the JD stays in draft. Before going live, many consultancies email the client for approval — use Pending review after you send that email. Careers RPC only lists jobs that are published and ops-open.';

export function buildClientListingApprovalMailto(params: {
  clientEmail: string | null | undefined;
  jobTitle: string;
  orgName?: string | null;
}): string | null {
  const to = params.clientEmail?.trim();
  if (!to) return null;
  const subject = `Approval to publish role: ${params.jobTitle}`;
  const body = `Hi,

Please confirm that we may list the following role on our public careers site and share the posting link with candidates:

Role: ${params.jobTitle}
${params.orgName ? `Organisation (consultancy): ${params.orgName}\n` : ''}
Reply with your approval or requested changes.

Thank you.`;
  return `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
